import React, { useState } from "react";
import { ApiEndpoints } from "../../../../core";
import {
  useGetExercisesQuery,
  useGetSessionFormatBloacksBySessionFormatIdQuery,
  useDeleteSessionMatriceGroupBlockMutation,
} from "../../../../services";
import DeleteBlockModal from "../modals/DeleteBlockModal";

interface Exercise {
  id: string;
  name: string;
  muscle: string;
  equipment: string;
  img: string;
  thumbnail?: { key: string };
  muscles?: { name: string }[];
  type?: string;
}

interface FormatBlockProps {
  themeBox: boolean;
  muscles: { _id: string; name: string }[];
  equipments: { _id: string; name: string }[];
  formatId?: string;
  difficulty?: string;
  beginnerSessionData: {
    workout: string;
    order: number;
    difficulty: string;
  }[]; // updated type
  setBeginnerSessionData: (
    data: {
      workout: string;
      order: number;
      difficulty: string;
    }[],
  ) => void; // updated type
  advancedSessionData: {
    workout: string;
    order: number;
    difficulty: string;
  }[]; // new prop
  setAdvancedSessionData: (
    data: {
      workout: string;
      order: number;
      difficulty: string;
    }[],
  ) => void; // new prop
  currentDifficulty: string; // "BEGINNER" | "ADVANCED"
  setHasExistingBlocks: (has: boolean) => void; // NEW: Callback for existing blocks
  onDirtyChange?: (isDirty: boolean) => void;
}

const FormatBlock: React.FC<FormatBlockProps> = ({
  themeBox,
  muscles,
  equipments,
  formatId,
  difficulty,
  beginnerSessionData,
  setBeginnerSessionData,
  advancedSessionData,
  setAdvancedSessionData,
  currentDifficulty,
  setHasExistingBlocks,
  onDirtyChange,
}) => {
  const [selectedMuscleId, setSelectedMuscleId] = useState("");
  const [selectedEquipmentId, setSelectedEquipmentId] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [search, setSearch] = useState("");
  const [muscle, setMuscle] = useState("");
  const [equipment, setEquipment] = useState("");

  // Pre-filled (API) lists now store the full block objects
  const [beginnerPrefilled, setBeginnerPrefilled] = useState<any[]>([]);
  const [advancedPrefilled, setAdvancedPrefilled] = useState<any[]>([]);
  const [beginnerNew, setBeginnerNew] = useState<string[]>([]);
  const [advancedNew, setAdvancedNew] = useState<string[]>([]);
  // Track removed SINGLE block IDs that need to be deleted
  const [removedBeginnerIds, setRemovedBeginnerIds] = useState<string[]>([]);
  const [removedAdvancedIds, setRemovedAdvancedIds] = useState<string[]>([]);

  // Combined ordering states to allow interleaving prefilled blocks and new exercises
  const [beginnerOrder, setBeginnerOrder] = useState<
    { id: string; type: "prefilled" | "new" }[]
  >([]);
  const [advancedOrder, setAdvancedOrder] = useState<
    { id: string; type: "prefilled" | "new" }[]
  >([]);

  const { data: sessionFormatBlocks, refetch: refetchFormatBlocks } =
    useGetSessionFormatBloacksBySessionFormatIdQuery(
      {
        sessionFormatId: formatId || "",
        difficulty: difficulty as any,
      },
      { skip: !formatId },
    );

  // Mutation for deleting workout from group
  const [deleteGroupWorkout, { isLoading: isDeletingGroup }] =
    useDeleteSessionMatriceGroupBlockMutation();
  // Reset lists only when formatId changes (not on difficulty switch)
  React.useEffect(() => {
    setBeginnerPrefilled([]);
    setAdvancedPrefilled([]);
    setBeginnerNew([]);
    setAdvancedNew([]);
    setBeginnerSessionData([]);
    setAdvancedSessionData([]);
    setRemovedBeginnerIds([]);
    setRemovedAdvancedIds([]);
    setBeginnerOrder([]);
    setAdvancedOrder([]);
  }, [formatId, setBeginnerSessionData, setAdvancedSessionData]);

  // Initialize lists with existing data when sessionFormatBlocks loads
  React.useEffect(() => {
    if (
      sessionFormatBlocks?.results &&
      sessionFormatBlocks.results.length > 0
    ) {
      setHasExistingBlocks(true); // Notify parent that existing blocks found

      const beginnerWorkouts: string[] = [];
      const advancedWorkouts: string[] = [];

      sessionFormatBlocks.results.forEach((block: any) => {
        if (block.workouts && block.workouts.length > 0) {
          const workoutId =
            block.workouts[0]._id || block.workouts[0].id || block.workouts[0];
          if (block.difficulty === "BEGINNER") {
            beginnerWorkouts.push(workoutId);
          } else if (
            block.difficulty === "ADVANCED" ||
            block.difficulty === "GENERAL"
          ) {
            advancedWorkouts.push(workoutId);
          }
        }
      });

      // Sort by order to maintain sequence
      const sortedBeginnerBlocks = sessionFormatBlocks.results
        .filter((block: any) => block.difficulty === "BEGINNER")
        .sort((a: any, b: any) => a.order - b.order);

      const sortedAdvancedBlocks = sessionFormatBlocks.results
        .filter(
          (block: any) =>
            block.difficulty === "ADVANCED" || block.difficulty === "GENERAL",
        )
        .sort((a: any, b: any) => a.order - b.order);

      setBeginnerPrefilled(sortedBeginnerBlocks);
      setAdvancedPrefilled(sortedAdvancedBlocks);

      setBeginnerOrder(
        sortedBeginnerBlocks.map((b: any) => ({
          id: b._id,
          type: "prefilled",
        })),
      );
      setAdvancedOrder(
        sortedAdvancedBlocks.map((b: any) => ({
          id: b._id,
          type: "prefilled",
        })),
      );
    } else {
      // Reset lists when no existing data
      setHasExistingBlocks(false);
      setBeginnerPrefilled([]);
      setAdvancedPrefilled([]);
      setBeginnerOrder([]);
      setAdvancedOrder([]);
    }
  }, [sessionFormatBlocks, setHasExistingBlocks]);


  // Check for dirty state
  React.useEffect(() => {
    const isDirty =
      beginnerNew.length > 0 ||
      removedBeginnerIds.length > 0 ||
      advancedNew.length > 0 ||
      removedAdvancedIds.length > 0;
    if (onDirtyChange) {
      onDirtyChange(isDirty);
    }
  }, [
    beginnerNew,
    removedBeginnerIds,
    advancedNew,
    removedAdvancedIds,
    onDirtyChange,
  ]);

  const [, forceUpdate] = useState(0);

  React.useEffect(() => {
    // Sync logic using beginnerOrder to allow interleaving
    const allWorkouts = beginnerOrder
      .filter((item: any) => {
        if (item.type === "prefilled")
          return !removedBeginnerIds.includes(item.id);
        return beginnerNew.includes(item.id);
      })
      .map((item: any, idx: number) => {
        if (item.type === "prefilled") {
          const block = beginnerPrefilled.find((b: any) => b._id === item.id);
          const firstWorkout = block?.workouts?.[0];
          const workoutId =
            firstWorkout?.workout?._id ||
            firstWorkout?.workout?.id ||
            firstWorkout?.workout ||
            firstWorkout?._id ||
            firstWorkout;
          return {
            workout: workoutId,
            order: idx + 1,
            difficulty: "BEGINNER",
          };
        } else {
          return {
            workout: item.id,
            order: idx + 1,
            difficulty: "BEGINNER",
          };
        }
      });

    setBeginnerSessionData(allWorkouts);
    forceUpdate((v) => v + 1);
  }, [
    beginnerOrder,
    beginnerNew,
    beginnerPrefilled,
    removedBeginnerIds,
    setBeginnerSessionData,
  ]);

  React.useEffect(() => {
    // Sync logic using advancedOrder to allow interleaving
    const allWorkouts = advancedOrder
      .filter((item: any) => {
        if (item.type === "prefilled")
          return !removedAdvancedIds.includes(item.id);
        return advancedNew.includes(item.id);
      })
      .map((item: any, idx: number) => {
        const diff = currentDifficulty === "GENERAL" ? "GENERAL" : "ADVANCED";
        if (item.type === "prefilled") {
          const block = advancedPrefilled.find((b: any) => b._id === item.id);
          const firstWorkout = block?.workouts?.[0];
          const workoutId =
            firstWorkout?.workout?._id ||
            firstWorkout?.workout?.id ||
            firstWorkout?.workout ||
            firstWorkout?._id ||
            firstWorkout;
          return {
            workout: workoutId,
            order: idx + 1,
            difficulty: diff,
          };
        } else {
          return {
            workout: item.id,
            order: idx + 1,
            difficulty: diff,
          };
        }
      });

    setAdvancedSessionData(allWorkouts);
    forceUpdate((v) => v + 1);
  }, [
    advancedOrder,
    advancedNew,
    advancedPrefilled,
    removedAdvancedIds,
    currentDifficulty,
    setAdvancedSessionData,
  ]);

  // Add a state for showing duplicate message
  const [duplicateMsg, setDuplicateMsg] = useState<string | null>(null);

  // Helper to get all selected workout ids for current format & difficulty
  const getAllSelectedWorkoutIds = () => {
    const ids = new Set<string>();
    // Prefilled (now blocks)
    let prefilled =
      currentDifficulty === "BEGINNER" ? beginnerPrefilled : advancedPrefilled;

    // Filter out removed blocks
    const removedIds =
      currentDifficulty === "BEGINNER"
        ? removedBeginnerIds
        : removedAdvancedIds;

    prefilled = prefilled.filter((block) => !removedIds.includes(block._id));

    prefilled.forEach((block: any) => {
      if (block.workouts && Array.isArray(block.workouts)) {
        block.workouts.forEach((w: any) => {
          const wId =
            w.workout?._id || w.workout?.id || w.workout || w._id || w;
          if (typeof wId === "string") ids.add(wId);
        });
      }
    });

    // New
    const newOnes =
      currentDifficulty === "BEGINNER" ? beginnerNew : advancedNew;
    newOnes.forEach((id) => ids.add(id));
    return ids;
  };

  // Helper to get all selected workout ids from sessionFormatBlocks for current format & difficulty
  const getAllBlockWorkoutIds = () => {
    const ids = new Set<string>();
    // Track removed IDs for current session
    const removedIds =
      currentDifficulty === "BEGINNER"
        ? removedBeginnerIds
        : removedAdvancedIds;

    if (sessionFormatBlocks?.results?.length > 0) {
      sessionFormatBlocks.results
        .filter(
          (block: any) =>
            block.format === formatId &&
            block.difficulty === currentDifficulty &&
            !removedIds.includes(block._id), // exclude removed blocks
        )
        .forEach((block: any) => {
          if (Array.isArray(block.workouts)) {
            block.workouts.forEach((w: any) => {
              if (w && w.workout && w.workout._id) {
                ids.add(w.workout._id);
              } else if (typeof w === "string") {
                ids.add(w);
              } else if (w && w._id) {
                ids.add(w._id);
              }
            });
          }
        });
    }
    return ids;
  };

  // Store details of selected exercises so they persist even if filters change
  const [selectedExercisesDetails, setSelectedExercisesDetails] = useState<
    Exercise[]
  >([]);

  // Drag states
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [draggedExerciseId, setDraggedExerciseId] = useState<string | null>(
    null,
  );

  // Confirmation Modal State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{
    id: string;
    type: "new" | "prefilled";
    block?: any;
    workoutId?: string;
    name: string;
  } | null>(null);

  const [page, setPage] = useState(1);
  const [exercisesList, setExercisesList] = useState<Exercise[]>([]);
  const [hasMore, setHasMore] = useState(true);

  // Reset pagination when filters or search change
  React.useEffect(() => {
    setPage(1);
    setExercisesList([]);
    setHasMore(true);
  }, [selectedMuscleId, selectedEquipmentId, searchValue]);

  // Fetch exercises (Available on left panel)
  const { data: exerciseData, isFetching: isFetchingExercises } = useGetExercisesQuery({
    muscle: selectedMuscleId,
    equipment: selectedEquipmentId,
    name: searchValue,
    page: page,
    limit: 20,
  });

  // Load next page when reaching the end
  React.useEffect(() => {
    if (exerciseData?.results) {
      if (page === 1) {
        setExercisesList(exerciseData.results);
      } else {
        setExercisesList((prev) => {
          // Flatten if results contain duplicates (optional, based on API)
          const newResults = exerciseData.results.filter(
            (newEx: any) => !prev.some((ex) => (ex.id || (ex as any)._id) === (newEx.id || (newEx as any)._id))
          );
          return [...prev, ...newResults];
        });
      }

      // If we got fewer results than requested, we probably reached the end
      if (exerciseData.results.length < 20) {
        setHasMore(false);
      }
    }
  }, [exerciseData, page]);

  const exercises = exercisesList;

  // Handle intersection or scroll events
  const handleLeftScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight + 50 && hasMore && !isFetchingExercises) {
      setPage((prev) => prev + 1);
    }
  };

  // Helper to get exercise object by id
  const getExerciseById = (id: string): Exercise | undefined => {
    // 1. Check currently loaded exercises (filtered list)
    let ex = exercises.find(
      (e: Exercise) => e.id === id || (e as any)._id === id,
    );
    if (ex) return ex;

    // 2. Check locally stored details
    ex = selectedExercisesDetails.find(
      (e: Exercise) => e.id === id || (e as any)._id === id,
    );
    if (ex) return ex;

    // 3. Check session format blocks (saved ones)
    if (sessionFormatBlocks?.results?.length > 0) {
      for (const block of sessionFormatBlocks.results) {
        if (Array.isArray(block.workouts)) {

          for (const w of block.workouts) {
            if (
              typeof w === "object" &&
              w.workout &&
              (w.workout._id === id ||
                w.workout.id === id ||
                w.workout === id ||
                w._id === id)
            ) {
              return {
                id: w.workout._id || w.workout.id || w.workout || w._id,
                name: w.workout.name || w.name || "Exercise",
                muscle: "",
                equipment: "",
                img: "",
                thumbnail: w.workout.thumbnail
                  ? { key: w.workout.thumbnail.key }
                  : undefined,
                // Use the workout's content type (CUSTOM/DEFAULT) so image URL resolves correctly
                type: w.workout.type || w.type,
              };
            }
          }
        }
      }
    }
    return ex;
  };

  // Add Exercise
  function addExercise(id: string) {
    const allSelectedIds = getAllSelectedWorkoutIds();
    const allBlockIds = getAllBlockWorkoutIds();
    if (allSelectedIds.has(id) || allBlockIds.has(id)) {
      setDuplicateMsg("Workout already exists in this format and difficulty.");
      setTimeout(() => setDuplicateMsg(null), 2000);
      return;
    }

    // Capture the exercise details before adding ID
    const exerciseDetail = exercises.find(
      (e: Exercise) => e.id === id || (e as any)._id === id,
    );
    if (exerciseDetail) {
      setSelectedExercisesDetails((prev) => {
        // Avoid duplicates in details array
        if (prev.find((e) => e.id === id || (e as any)._id === id)) return prev;
        return [...prev, exerciseDetail];
      });
    }

    if (currentDifficulty === "BEGINNER") {
      setBeginnerNew((prev) => [...prev, id]);
      setBeginnerOrder((prev) => [...prev, { id, type: "new" }]);
    } else {
      setAdvancedNew((prev) => [...prev, id]);
      setAdvancedOrder((prev) => [...prev, { id, type: "new" }]);
    }
  }

  // Prevent drag if already exists
  const isExerciseDisabled = (exerciseId: string) => {
    const allSelectedIds = getAllSelectedWorkoutIds();
    const allBlockIds = getAllBlockWorkoutIds();
    return allSelectedIds.has(exerciseId) || allBlockIds.has(exerciseId);
  };

  // Remove only from new list
  function removeExercise(id: string) {
    const ex = getExerciseById(id);
    setItemToDelete({
      id,
      type: "new",
      name: ex?.name || "Exercise",
    });
    setShowDeleteModal(true);
  }

  // Remove prefilled exercise (GROUP or SINGLE)
  const handleRemovePrefilled = (block: any, workoutId?: string) => {
    let name = block.name || "Group";
    if (workoutId) {
      const ex = getExerciseById(workoutId);
      name = ex?.name || name;
    } else {
      const firstWorkout = block.workouts?.[0];
      const wId =
        firstWorkout?.workout?._id ||
        firstWorkout?.workout?.id ||
        firstWorkout?.workout ||
        firstWorkout?._id ||
        firstWorkout;
      if (wId) {
        const ex = getExerciseById(wId);
        name = ex?.name || name;
      }
    }

    setItemToDelete({
      id: block._id,
      type: "prefilled",
      block,
      workoutId,
      name,
    });
    setShowDeleteModal(true);
  };

  const confirmRemove = async () => {
    if (!itemToDelete) return;

    const { id, type, block, workoutId } = itemToDelete;

    if (type === "new") {
      if (currentDifficulty === "BEGINNER") {
        setBeginnerNew((prev) => prev.filter((exId) => exId !== id));
        setBeginnerOrder((prev) => prev.filter((item) => item.id !== id));
      } else {
        setAdvancedNew((prev) => prev.filter((exId) => exId !== id));
        setAdvancedOrder((prev) => prev.filter((item) => item.id !== id));
      }
    } else {
      // PREFILLED
      if (block.type === "GROUP" && workoutId) {
        try {
          await deleteGroupWorkout({
            groupId: block._id,
            workoutId: workoutId,
          }).unwrap();

          if (currentDifficulty === "BEGINNER") {
            setBeginnerPrefilled((prev) =>
              prev.map((b) =>
                b._id === block._id && b.workouts
                  ? {
                    ...b,
                    workouts: b.workouts.filter((w: any) => {
                      const wId =
                        w.workout?._id ||
                        w.workout?.id ||
                        w.workout ||
                        w._id ||
                        w;
                      return wId !== workoutId;
                    }),
                  }
                  : b,
              ),
            );
          } else {
            setAdvancedPrefilled((prev) =>
              prev.map((b) =>
                b._id === block._id && b.workouts
                  ? {
                    ...b,
                    workouts: b.workouts.filter((w: any) => {
                      const wId =
                        w.workout?._id ||
                        w.workout?.id ||
                        w.workout ||
                        w._id ||
                        w;
                      return wId !== workoutId;
                    }),
                  }
                  : b,
              ),
            );
          }
        } catch (error) {
          console.error("Failed to delete workout from group:", error);
        }
      } else {
        // SINGLE block
        if (currentDifficulty === "BEGINNER") {
          setBeginnerPrefilled((prev) =>
            prev.filter((b) => b._id !== block._id),
          );
          setRemovedBeginnerIds((prev) => [...prev, block._id]);
          setBeginnerOrder((prev) =>
            prev.filter((item) => item.id !== block._id),
          );
        } else {
          setAdvancedPrefilled((prev) =>
            prev.filter((b) => b._id !== block._id),
          );
          setRemovedAdvancedIds((prev) => [...prev, block._id]);
          setAdvancedOrder((prev) =>
            prev.filter((item) => item.id !== block._id),
          );
        }
      }
    }

    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  // Drag handlers
  const onDragStart = (id: string) => setDraggingId(id);
  const onDragOver = (e: React.DragEvent<HTMLDivElement>, overId: string) => {
    e.preventDefault();
    if (!draggingId || draggingId === overId) return;

    if (currentDifficulty === "BEGINNER") {
      setBeginnerOrder((prev) => {
        const fromIdx = prev.findIndex((item) => item.id === draggingId);
        const toIdx = prev.findIndex((item) => item.id === overId);
        if (fromIdx === -1 || toIdx === -1) return prev;
        const copy = [...prev];
        const [movedItem] = copy.splice(fromIdx, 1);
        copy.splice(toIdx, 0, movedItem);
        return copy;
      });
    } else {
      setAdvancedOrder((prev) => {
        const fromIdx = prev.findIndex((item) => item.id === draggingId);
        const toIdx = prev.findIndex((item) => item.id === overId);
        if (fromIdx === -1 || toIdx === -1) return prev;
        const copy = [...prev];
        const [movedItem] = copy.splice(fromIdx, 1);
        copy.splice(toIdx, 0, movedItem);
        return copy;
      });
    }
  };
  const onDragEnd = () => setDraggingId(null);
  const onLeftDragStart = (id: string) => setDraggedExerciseId(id);
  const onRightDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (draggedExerciseId) {
      addExercise(draggedExerciseId);
    }
    setDraggedExerciseId(null);
  };

  // Lists for rendering
  const selectedPrefilled =
    currentDifficulty === "BEGINNER" ? beginnerPrefilled : advancedPrefilled;
  const selectedNew =
    currentDifficulty === "BEGINNER" ? beginnerNew : advancedNew;
  const selectedList = [...selectedPrefilled, ...selectedNew];

  return (
    <div className="flex">
      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 5px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { 
          background: ${themeBox ? "#333333" : "#D1D5DB"}; 
          border-radius: 10px; 
        }
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: ${themeBox ? "#333333" : "#D1D5DB"} transparent;
        }
      `}</style>
      <div className="flex-1 p-4">
        {/* Filters */}
        <div className="space-y-3 mb-4">
          <div>
            <label
              className={`text-[11px] font-medium ${themeBox ? "text-gray-300" : "text-gray-700"
                }`}
            >
              Search Exercise Here
            </label>
            <div
              className={`flex items-center mt-1 rounded-[10px] ${themeBox
                ? "bg-[#1F1F1F] border border-[#9B9DB6]"
                : "bg-white border border-gray-300"
                } px-2`}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z"
                  stroke="#949494"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M21 21L17 17"
                  stroke="#949494"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <input
                className={`flex-1 px-2 py-1.5 text-xs outline-none ${themeBox
                  ? "bg-transparent text-gray-200"
                  : "bg-transparent text-gray-800"
                  }`}
                placeholder="Type to search..."
                value={searchValue}
                onChange={(e) => {
                  setSearchValue(e.target.value);
                  setSearch(e.target.value);
                }}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label
                className={`text-[11px] font-medium ${themeBox ? "text-gray-300" : "text-gray-700"
                  }`}
              >
                Muscle
              </label>
              <select
                className={`mt-1 w-full rounded-[10px] px-2 py-1.5 text-xs outline-none ${themeBox
                  ? "bg-[#1F1F1F] border border-[#9B9DB6] text-gray-200"
                  : "bg-white border border-gray-300 text-gray-800"
                  }`}
                value={selectedMuscleId}
                onChange={(e) => {
                  setSelectedMuscleId(e.target.value);
                  setMuscle(e.target.value);
                }}
              >
                <option value="">All</option>
                {muscles.map((m) => (
                  <option key={m._id} value={m._id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                className={`text-[11px] font-medium ${themeBox ? "text-gray-300" : "text-gray-700"
                  }`}
              >
                Equipment
              </label>
              <select
                className={`mt-1 w-full rounded-[10px] px-2 py-1.5 text-xs outline-none ${themeBox
                  ? "bg-[#1F1F1F] border border-[#9B9DB6] text-gray-200"
                  : "bg-white border border-gray-300 text-gray-800"
                  }`}
                value={selectedEquipmentId}
                onChange={(e) => {
                  setSelectedEquipmentId(e.target.value);
                  setEquipment(e.target.value);
                }}
              >
                <option value="">All</option>
                {equipments.map((eq) => (
                  <option key={eq._id} value={eq._id}>
                    {eq.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Choose Exercises */}
        <div>
          <label
            className={`text-[11px] font-medium ${themeBox ? "text-gray-300" : "text-gray-700"
              }`}
          >
            Choose Exercises
          </label>
          <div
            onScroll={handleLeftScroll}
            className={`mt-2 rounded-lg border ${themeBox
              ? "border-[#232323] bg-[#0D0D0D]"
              : "border-gray-300 bg-white"
              } max-h-[450px] overflow-y-auto custom-scrollbar p-3`}
          >
            {exercises.map((ex: Exercise) => {
              const exerciseId = ex.id || (ex as any)._id;
              const disabled = isExerciseDisabled(exerciseId);
              let imgSrc;
              if (ex.thumbnail?.key) {
                if (ex.type === "DEFAULT") {
                  imgSrc = `${ApiEndpoints.defaultWorkoutThumbnailURL}/${ex.thumbnail.key}`;
                } else {
                  imgSrc = `${ApiEndpoints.customWorkoutThumbnailURL}/${ex.thumbnail.key}`;
                }
              } else if (ex.img) {
                imgSrc = ex.img;
              }
              return (
                <div
                  key={exerciseId}
                  draggable={!disabled}
                  onDragStart={
                    !disabled ? () => onLeftDragStart(exerciseId) : undefined
                  }
                  className={`flex items-center gap-3 px-3 py-2 text-xs mb-2 rounded-[10px] border ${themeBox
                    ? "border-[#9B9DB6] bg-[#1F1F1F] hover:bg-[#252525]"
                    : "border-gray-200 bg-white hover:bg-gray-50"
                    } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-grab"
                    } transition-colors`}
                >
                  <div className="flex items-center">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <circle cx="5" cy="7" r="1.5" fill="#4B4B4B" />
                      <circle cx="5" cy="12" r="1.5" fill="#4B4B4B" />
                      <circle cx="5" cy="17" r="1.5" fill="#4B4B4B" />
                      <circle cx="12" cy="7" r="1.5" fill="#4B4B4B" />
                      <circle cx="12" cy="12" r="1.5" fill="#4B4B4B" />
                      <circle cx="12" cy="17" r="1.5" fill="#4B4B4B" />
                    </svg>
                  </div>
                  <div className="w-10 h-10 rounded overflow-hidden bg-black/20 flex-shrink-0">
                    <img
                      src={imgSrc}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <span
                    className={`flex-1 font-medium ${themeBox ? "text-gray-200" : "text-gray-800"
                      }`}
                  >
                    {ex.name}
                  </span>
                  {!disabled && (
                    <button
                      type="button"
                      onClick={() => addExercise(exerciseId)}
                      className="p-1 hover:bg-white/5 rounded-full transition-colors"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M18 6L6.00081 17.9992M17.9992 18L6 6.00085"
                          stroke={themeBox ? "#949494" : "#666666"}
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  )}
                  {disabled && (
                    <div className="p-1">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        className="opacity-30"
                      >
                        <path
                          d="M12 5V19M5 12H19"
                          stroke={themeBox ? "#949494" : "#666666"}
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>
                  )}
                </div>
              );
            })}
            {exercises.length === 0 && !isFetchingExercises && (
              <div
                className={`px-3 py-4 text-center text-xs ${themeBox ? "text-gray-500" : "text-gray-500"
                  }`}
              >
                No matches
              </div>
            )}
            {isFetchingExercises && (
              <div className="flex justify-center p-4">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#B6CB24]"></div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div
        className={`w-[420px] p-4 border-l ${themeBox ? "border-[#232323]" : "border-gray-200"
          } flex flex-col`}
        // Enable drop on right panel
        onDragOver={(e) => e.preventDefault()}
        onDrop={onRightDrop}
      >
        <label
          className={`text-[11px] font-medium mb-2 ${themeBox ? "text-gray-300" : "text-gray-700"
            }`}
        >
          Selected Exercises
        </label>
        <div
          className={`flex-1 rounded-xl border border-dashed ${themeBox
            ? "border-[#444] bg-[#0D0D0D]"
            : "border-gray-300 bg-white"
            } overflow-y-auto p-3 custom-scrollbar ${selectedList.length > 8 ? "max-h-[550px]" : "min-h-[400px]"
            }`}
        >
          {selectedList.length === 0 && (
            <div
              className={`h-full flex items-center align-center justify-center text-xs ${themeBox ? "text-gray-500" : "text-gray-500"
                }`}
            >
              Drag exercises here to add to your session.
            </div>
          )}
          {/* Interleaved Pre-filled and New Items */}
          {(currentDifficulty === "BEGINNER"
            ? beginnerOrder
            : advancedOrder
          ).map((item: any) => {
            if (item.type === "prefilled") {
              const block = (
                currentDifficulty === "BEGINNER"
                  ? beginnerPrefilled
                  : advancedPrefilled
              ).find((b: any) => b._id === item.id);
              if (!block) return null;

              if (block.type === "GROUP") {
                return (
                  <div
                    key={block._id}
                    draggable
                    onDragStart={() => onDragStart(block._id)}
                    onDragOver={(e) => onDragOver(e, block._id)}
                    onDragEnd={onDragEnd}
                    className={`mb-3 rounded-[10px] border cursor-grab transition-all ${themeBox
                      ? "border-[#9B9DB6] bg-[#1F1F1F]"
                      : "border-gray-200 bg-gray-50"
                      } ${draggingId === block._id ? "opacity-40" : ""}`}
                  >
                    <div
                      className={` flex items-center gap-2 px-3 py-2 text-xs font-semibold ${themeBox
                        ? "text-gray-300 border-b border-[#9B9DB6]"
                        : "text-gray-700 border-b border-gray-200"
                        }`}
                    >
                      <div className="cursor-grab">
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <circle cx="5" cy="7" r="1.5" fill="#4B4B4B" />
                          <circle cx="5" cy="12" r="1.5" fill="#4B4B4B" />
                          <circle cx="5" cy="17" r="1.5" fill="#4B4B4B" />
                          <circle cx="12" cy="7" r="1.5" fill="#4B4B4B" />
                          <circle cx="12" cy="12" r="1.5" fill="#4B4B4B" />
                          <circle cx="12" cy="17" r="1.5" fill="#4B4B4B" />
                        </svg>
                      </div>

                      {block.name || "Group"}
                    </div>
                    <div>
                      {block.workouts?.map((w: any, index: number) => {
                        const workoutId =
                          w.workout?._id ||
                          w.workout?.id ||
                          w.workout ||
                          w._id ||
                          w;
                        const ex = getExerciseById(workoutId);

                        if (!ex) return null;
                        let imgSrc;
                        if (ex.thumbnail?.key) {
                          if (ex.type === "DEFAULT") {
                            imgSrc = `${ApiEndpoints.defaultWorkoutThumbnailURL}/${ex.thumbnail.key}`;
                          } else {
                            imgSrc = `${ApiEndpoints.customWorkoutThumbnailURL}/${ex.thumbnail.key}`;
                          }
                        }

                        return (
                          <div
                            key={workoutId + "_" + index}
                            className={`flex items-center gap-3 px-3 py-2 text-xs opacity-70`}
                          >
                            <div className="w-8 h-8 rounded overflow-hidden bg-black/20 ml-6 flex-shrink-0">
                              <img
                                src={imgSrc}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <span
                              className={`flex-1 font-medium ${themeBox ? "text-gray-400" : "text-gray-600"
                                }`}
                            >
                              {ex.name}
                            </span>
                            <button
                              type="button"
                              onClick={() =>
                                handleRemovePrefilled(block, workoutId)
                              }
                              className={`p-1 hover:bg-white/5 rounded-full transition-colors`}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                              >
                                <path
                                  d="M18 6L6.00081 17.9992M17.9992 18L6 6.00085"
                                  stroke={themeBox ? "#949494" : "#666666"}
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              }

              // Single Block View
              const firstWorkout = block.workouts?.[0];
              const workoutId =
                firstWorkout?.workout?._id ||
                firstWorkout?.workout?.id ||
                firstWorkout?.workout ||
                firstWorkout?._id ||
                firstWorkout;

              if (!workoutId) return null;
              const ex = getExerciseById(workoutId);
              if (!ex) return null;
              let imgSrc;
              if (ex.thumbnail?.key) {
                if (ex.type === "DEFAULT") {
                  imgSrc = `${ApiEndpoints.defaultWorkoutThumbnailURL}/${ex.thumbnail.key}`;
                } else {
                  imgSrc = `${ApiEndpoints.customWorkoutThumbnailURL}/${ex.thumbnail.key}`;
                }
              }
              return (
                <div
                  key={block._id}
                  draggable
                  onDragStart={() => onDragStart(block._id)}
                  onDragOver={(e) => onDragOver(e, block._id)}
                  onDragEnd={onDragEnd}
                  className={`flex items-center gap-3 px-3 py-2 text-xs cursor-grab mb-2 rounded-[10px] border transition-all ${themeBox
                    ? "border-[#9B9DB6] bg-[#1F1F1F]"
                    : "border-gray-200 bg-white"
                    } ${draggingId === block._id ? "opacity-40" : ""}`}
                >
                  <div className="cursor-grab">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <circle cx="5" cy="7" r="1.5" fill="#4B4B4B" />
                      <circle cx="5" cy="12" r="1.5" fill="#4B4B4B" />
                      <circle cx="5" cy="17" r="1.5" fill="#4B4B4B" />
                      <circle cx="12" cy="7" r="1.5" fill="#4B4B4B" />
                      <circle cx="12" cy="12" r="1.5" fill="#4B4B4B" />
                      <circle cx="12" cy="17" r="1.5" fill="#4B4B4B" />
                    </svg>
                  </div>
                  <div className="w-10 h-10 rounded overflow-hidden bg-black/20 flex-shrink-0">
                    <img
                      src={imgSrc}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <span
                    className={`flex-1 font-medium ${themeBox ? "text-gray-200" : "text-gray-800"
                      }`}
                  >
                    {ex.name}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleRemovePrefilled(block)}
                    className={`p-1 hover:bg-white/5 rounded-full transition-colors`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M18 6L6.00081 17.9992M17.9992 18L6 6.00085"
                        stroke={themeBox ? "#949494" : "#666666"}
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              );
            } else {
              // New item view
              const id = item.id;
              const ex = getExerciseById(id);
              if (!ex) return null;
              let imgSrc;
              if (ex.thumbnail?.key) {
                if (ex.type === "DEFAULT") {
                  imgSrc = `${ApiEndpoints.defaultWorkoutThumbnailURL}/${ex.thumbnail.key}`;
                } else {
                  imgSrc = `${ApiEndpoints.customWorkoutThumbnailURL}/${ex.thumbnail.key}`;
                }
              }
              return (
                <div
                  key={id}
                  draggable
                  onDragStart={() => onDragStart(id)}
                  onDragOver={(e) => onDragOver(e, id)}
                  onDragEnd={onDragEnd}
                  className={`flex items-center gap-3 px-3 py-2 text-xs cursor-grab mb-2 rounded-[10px] border transition-all ${themeBox
                    ? "border-[#9B9DB6] bg-[#1F1F1F]"
                    : "border-gray-200 bg-white"
                    } ${draggingId === id ? "opacity-50" : ""}`}
                >
                  <div className="cursor-grab">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <circle cx="5" cy="7" r="1.5" fill="#4B4B4B" />
                      <circle cx="5" cy="12" r="1.5" fill="#4B4B4B" />
                      <circle cx="5" cy="17" r="1.5" fill="#4B4B4B" />
                      <circle cx="12" cy="7" r="1.5" fill="#4B4B4B" />
                      <circle cx="12" cy="12" r="1.5" fill="#4B4B4B" />
                      <circle cx="12" cy="17" r="1.5" fill="#4B4B4B" />
                    </svg>
                  </div>
                  <div className="w-10 h-10 rounded overflow-hidden bg-black/20 flex-shrink-0">
                    <img
                      src={imgSrc}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <span
                    className={`flex-1 font-medium ${themeBox ? "text-gray-200" : "text-gray-800"
                      }`}
                  >
                    {ex.name}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeExercise(id)}
                    className={`p-1 hover:bg-white/5 rounded-full transition-colors`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M18 6L6.00081 17.9992M17.9992 18L6 6.00085"
                        stroke={themeBox ? "#949494" : "#666666"}
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              );
            }
          })}
        </div>
        {/* <div className="mt-4 flex justify-end">
          <button
            type="button"
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold border border-[#B6CB24] text-black shadow-sm"
            style={{
              background:
                "linear-gradient(92deg, rgba(237,245,192,0.90) 46.67%, rgba(198,217,44,0.90) 116.75%)",
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M21.544 11.045C21.848 11.4713 22 11.6845 22 12C22 12.3155 21.848 12.5287 21.544 12.955C20.1779 14.8706 16.6892 19 12 19C7.31078 19 3.8221 14.8706 2.45604 12.955C2.15201 12.5287 2 12.3155 2 12C2 11.6845 2.15201 11.4713 2.45604 11.045C3.8221 9.12944 7.31078 5 12 5C16.6892 5 20.1779 9.12944 21.544 11.045Z"
                stroke="#1F1F1F"
                strokeWidth="1.5"
              />
              <path
                d="M15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15C13.6569 15 15 13.6569 15 12Z"
                stroke="#1F1F1F"
                strokeWidth="1.5"
              />
            </svg>
            Preview Targeted Muscles
          </button>
        </div> */}
      </div>

      {duplicateMsg && (
        <div className="absolute bottom-20 left-1/2 -translate-x-1/2 bg-red-500 text-white px-4 py-2 rounded-md shadow-lg z-50">
          {duplicateMsg}
        </div>
      )}

      <DeleteBlockModal
        open={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setItemToDelete(null);
        }}
        themeBox={themeBox}
        blockId={itemToDelete?.id || ""}
        blockName={itemToDelete?.name || ""}
        onConfirmDelete={confirmRemove}
        isDeleting={isDeletingGroup}
      />
    </div>
  );
};

export default FormatBlock;
